import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";
import type { CurrencyCode } from "@/types";

type CurrencySwitcherVariant = "ghost" | "outline" | "dark";
type MenuPlacement = "above" | "below";

interface MenuPos {
  top: number;
  left: number;
  width: number;
  placement: MenuPlacement;
}

interface CurrencySwitcherProps {
  variant?: CurrencySwitcherVariant;
  className?: string;
  align?: "left" | "right";
  testid?: string;
}

/**
 * Compact currency switcher.
 *
 * The dropdown menu renders into a React portal anchored to `document.body`
 * so it CAN'T be clipped by ancestor `overflow:hidden` containers (this was
 * happening in the admin + studio sidebar footers where the parent uses
 * h-[100dvh] overflow-hidden to lock the viewport).
 *
 * Placement: auto. We measure the trigger's bounding rect and open below
 * when there's room, above when there isn't. Re-measures on scroll/resize.
 *
 * Variants:
 *  - "ghost"   — bare button, navigation bars
 *  - "outline" — bordered pill, footer / sidebar
 *  - "dark"    — dark-on-light, public dark CTAs
 */
export default function CurrencySwitcher({
  variant = "ghost",
  className = "",
  align = "right",
  testid = "currency-switcher",
}: CurrencySwitcherProps) {
  const { currency, setCurrency, currencies, meta } = useCurrency();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPos>({ top: 0, left: 0, width: 0, placement: "below" });
  // iter49 P3-H: track keyboard-focused option index for ARIA listbox.
  // Arrow Up/Down move it, Enter/Space selects, Escape closes — the
  // canonical ARIA 1.2 listbox pattern. Resets to the currently-selected
  // currency every time the menu opens so the user starts at "their" row.
  const [focusIdx, setFocusIdx] = useState(0);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);

  // Re-measure position whenever the menu opens AND on scroll/resize while
  // it's open — keeps the dropdown anchored to the trigger as the user
  // scrolls underneath it.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const compute = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const menuH = 200;   // approx max height of the dropdown
      const spaceBelow = window.innerHeight - rect.bottom;
      const placement: MenuPlacement = spaceBelow < menuH + 16 ? "above" : "below";
      const menuW = 224;   // matches min-w-[14rem] below
      let left = align === "left" ? rect.left : rect.right - menuW;
      // Keep at least 8px off either edge.
      left = Math.max(8, Math.min(left, window.innerWidth - menuW - 8));
      const top = placement === "above" ? rect.top - 8 : rect.bottom + 8;
      setPos({ top, left, width: menuW, placement });
    };
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open, align]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      // iter49 P3-H: arrow-key navigation inside the listbox.
      if (e.key === "Escape") { setOpen(false); return; }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((i) => (i + 1) % Math.max(1, currencies.length));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((i) => (i - 1 + Math.max(1, currencies.length)) % Math.max(1, currencies.length));
        return;
      }
      if (e.key === "Home") {
        e.preventDefault();
        setFocusIdx(0);
        return;
      }
      if (e.key === "End") {
        e.preventDefault();
        setFocusIdx(Math.max(0, currencies.length - 1));
        return;
      }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const c = currencies[focusIdx];
        if (c?.code) {
          setCurrency(c.code);
          setOpen(false);
        }
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, focusIdx, currencies, setCurrency]);

  // When the menu opens, seed focus at the currently-selected currency so
  // the user starts on "their" row instead of the first option.
  useEffect(() => {
    if (!open) return;
    const idx = currencies.findIndex((c) => c.code === currency);
    setFocusIdx(idx >= 0 ? idx : 0);
  }, [open, currencies, currency]);

  const triggerBase = "inline-flex items-center gap-1.5 text-[12px] font-medium tracking-wide transition-colors";
  const triggerStyles: Record<CurrencySwitcherVariant, string> = {
    ghost:   "px-2.5 py-1.5 rounded-md text-[var(--ink-soft)] hover:text-[var(--ink)] hover:bg-[var(--hover)]",
    outline: "px-3 py-1.5 rounded-full border border-[var(--border)] text-[var(--ink)] hover:bg-[var(--hover)]",
    dark:    "px-3 py-1.5 rounded-full border border-white/20 text-white/85 hover:text-white hover:border-white/40",
  };

  const menu = open ? (
    <ul
      ref={menuRef}
      role="listbox"
      aria-label="Choose currency"
      className="fixed z-[9999] py-1 rounded-xl border border-[var(--border)] bg-white shadow-[0_12px_32px_-8px_rgba(0,0,0,0.18)]"
      style={{ top: pos.placement === "above" ? "auto" : pos.top, bottom: pos.placement === "above" ? window.innerHeight - pos.top : "auto", left: pos.left, width: pos.width, transform: pos.placement === "above" ? "translateY(0)" : undefined }}
      data-testid={`${testid}-menu`}
    >
      {currencies.map((c, i) => {
        const active = c.code === currency;
        const focused = i === focusIdx;
        return (
          <li key={c.code}>
            <button
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => { setCurrency(c.code as CurrencyCode); setOpen(false); }}
              onMouseEnter={() => setFocusIdx(i)}
              className={`flex items-center justify-between w-full px-3 py-2 text-[13px] transition-colors ${focused ? "bg-[var(--hover)]" : ""} ${active ? "text-[var(--ink)] font-semibold" : "text-[var(--ink-soft)]"}`}
              data-testid={`${testid}-option-${c.code}`}
            >
              <span className="flex items-center gap-2">
                <span className="w-6 text-center font-mono text-[12px] text-[var(--ink-soft)]">{c.symbol && c.symbol !== c.code ? c.symbol : ""}</span>
                <span>{c.code}</span>
                <span className="text-[11px] text-[var(--muted)]">{c.label}</span>
              </span>
              {c.code !== "USD" && (
                <span className="text-[11px] text-[var(--muted)] tabular-nums">{Number(c.rate || 0).toFixed(c.code === "AED" ? 4 : 3)}</span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  ) : null;

  return (
    <div className={`relative ${className}`} data-testid={testid}>
      <button
        ref={triggerRef}
        type="button"
        className={`${triggerBase} ${triggerStyles[variant] || triggerStyles.ghost}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testid}-trigger`}
      >
        {meta?.symbol && meta.symbol !== currency && (
          <span className="leading-none">{meta.symbol}</span>
        )}
        <span className="leading-none">{currency}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.8} />
      </button>
      {typeof document !== "undefined" && menu ? createPortal(menu, document.body) : null}
    </div>
  );
}
